import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = [{
  title: 'Información que Recopilamos',
  text: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
}, {
  title: 'Uso de la Información',
  text: 'Tu información personal se utiliza exclusivamente para: procesar tu pedido, enviarte el producto adquirido, y comunicarte actualizaciones importantes relacionadas con tu compra. No vendemos ni compartimos tus datos con terceros con fines de marketing.'
}, {
  title: 'Protección de Datos',
  text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal, incluyendo cifrado SSL en todas las transacciones y almacenamiento seguro de datos.'
}, {
  title: 'Cookies',
  text: 'Utilizamos cookies esenciales para el funcionamiento del sitio y cookies analíticas para mejorar tu experiencia. Puedes desactivar las cookies en la configuración de tu navegador.'
}, {
  title: 'Tus Derechos',
  text: 'Tenés derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, podés contactarnos a través de los canales indicados en el producto.'
}];
const termsContent = [{
  title: 'Producto Digital',
  text: 'El Protocolo Anti-Inflamatorio 21D es un producto digital informativo en formato PDF. Al completar la compra, recibirás acceso inmediato al material a través del correo electrónico proporcionado.'
}, {
  title: 'Uso Personal',
  text: 'El contenido adquirido es para uso personal y exclusivo del comprador. Queda prohibida la reproducción, distribución o reventa total o parcial del material sin autorización expresa.'
}, {
  title: 'Descargo Médico',
  text: 'Este producto tiene fines informativos y educativos. No sustituye el consejo, diagnóstico o tratamiento médico profesional. Consultá a tu médico antes de comenzar cualquier programa de alimentación, especialmente si tenés condiciones médicas preexistentes.'
}, {
  title: 'Política de Reembolso',
  text: 'Debido a la naturaleza digital del producto y su entrega inmediata, no se aceptan devoluciones una vez realizada la descarga. Si tenés problemas técnicos para acceder al material, te ayudaremos a resolverlos.'
}, {
  title: 'Resultados',
  text: 'Los resultados pueden variar según cada persona. Los testimonios y ejemplos mencionados no garantizan resultados específicos. El compromiso y la consistencia son factores clave para obtener beneficios del protocolo.'
}, {
  title: 'Modificaciones',
  text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor a partir de su publicación en el sitio.'
}];
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="bg-charcoal text-sage-100 py-12 text-center" data-id="element-216">
        <div className="max-w-4xl mx-auto px-6" data-id="element-217">
          <p className="font-serif text-xl mb-4 text-white" data-id="element-218">
            Protocolo Anti-Inflamatorio 21D
          </p>
          <p className="text-sm text-charcoal-light mb-6" data-id="element-219">
            © {new Date().getFullYear()} Todos los derechos reservados.
          </p>
          <p className="text-xs text-charcoal-light max-w-2xl mx-auto leading-relaxed mb-6" data-id="element-220">
            Descargo de responsabilidad: Este producto no sustituye el consejo
            médico profesional. Consulte a su médico antes de comenzar cualquier
            programa de alimentación o ejercicio, especialmente si tiene
            condiciones médicas preexistentes.
          </p>

          <div className="flex items-center justify-center gap-6 text-sm" data-id="element-221">
            <button onClick={() => setActiveModal('privacy')} className="text-sage-300 hover:text-white transition-colors duration-200 underline underline-offset-4 decoration-sage-600 hover:decoration-white" data-id="element-222">
              Política de Privacidad
            </button>
            <span className="text-sage-600" data-id="element-223">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sage-300 hover:text-white transition-colors duration-200 underline underline-offset-4 decoration-sage-600 hover:decoration-white" data-id="element-224">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-225">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm" onClick={closeModal} data-id="element-226">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-227">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-cream-200" data-id="element-228">
                <h2 className="text-2xl font-bold text-charcoal" data-id="element-229">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={closeModal} className="w-10 h-10 rounded-full bg-cream-100 hover:bg-cream-200 flex items-center justify-center transition-colors" aria-label="Cerrar" data-id="element-230">
                  <XIcon className="w-5 h-5 text-charcoal" data-id="element-231" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-232">
                {(activeModal === 'privacy' ? privacyContent : termsContent).map((section, index) => <div key={index} data-id="element-233">
                    <h3 className="text-lg font-bold text-charcoal mb-2 font-sans" data-id="element-234">
                      {section.title}
                    </h3>
                    <p className="text-charcoal-light leading-relaxed" data-id="element-235">
                      {section.text}
                    </p>
                  </div>)}

                <p className="text-sm text-charcoal-light pt-4 border-t border-cream-200" data-id="element-236">
                  Última actualización: Abril 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}